<template>
  <div class="chat-log-container d-flex flex-column">
    <div class="chat-log-list-wrapper">
      <div v-if="loadingChatLog" class="h-50 d-flex justify-center align-center">
        <v-progress-circular
          indeterminate
          color="grey"
          size="36"
          width="2"
        />
      </div>
      <div v-else class="chat-log-list" @copy.stop>
        <div v-if="!chatLog?.length" class="prompt">
          <div class="prompt-box ai-response-box">
            <div class="markdown-content">
              Welcome to the AI Copilot. Type instructions or questions to explore and adjust your workflow.
            </div>
          </div>
        </div>
        <div v-for="log in chatLog" :key="log.id" class="prompt" :class="{'user-prompt': isUserPrompt(log)}">
          <div v-if="isUserPrompt(log)" class="user-prompt-row">
            <div class="prompt-box user-prompt-box">
              <span class="chat-log-content">
                {{ log.content }}
              </span>
            </div>
            <v-btn
              v-if="!loadingResponse && log.workflowVersion"
              type="button"
              size="28"
              density="compact"
              variant="text"
              color="grey-darken-1"
              class="edit-btn"
              :disabled="loadingResponse || loadingChatLog"
              @click="startEditPromptFromLog(log)"
            >
              <v-icon size="16">mdi-pencil-outline</v-icon>
            </v-btn>
          </div>
          <div v-else class="prompt-box ai-response-box">
            <Markdown :source="log.content" class="markdown-content" />
          </div>
        </div>
        <div v-if="showPendingPrompt" class="prompt user-prompt">
          <div class="user-prompt-row">
            <div class="prompt-box user-prompt-box">
              <span class="chat-log-content">
                {{ pendingPrompt }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="streamingMessage" class="prompt">
          <div class="prompt-box ai-response-box">
            <Markdown :source="streamingMessage" class="markdown-content" />
          </div>
        </div>
        <div v-if="loadingResponse && !streamingMessage" class="assistant-status">
          <div
            v-for="(entry, index) in actionLog"
            :key="index"
            :class="[
              entry.kind === 'narration' ? 'assistant-thinking-text' : 'assistant-status-text',
              { 'assistant-status-failed': entry.failed }
            ]"
          >
            {{ entry.text }}
            <div v-if="entry.error" class="assistant-status-reason">{{ entry.error }}</div>
          </div>
          <div class="assistant-status-row">
            <dots-loader />
            <div v-if="assistantStatusText" class="assistant-status-text">
              {{ assistantStatusText }}
            </div>
          </div>
        </div>
      </div>
      <div ref="scrollTargetRef" />
    </div>
    <div>
      <div v-if="canContinue" class="suggestion-chips">
        <v-chip
          size="small"
          variant="flat"
          color="primary"
          class="suggestion-chip"
          prepend-icon="mdi-play"
          :disabled="loadingResponse || isEditingPrompt"
          @click="submitSuggestion(CONTINUE_PROMPT)"
        >
          Yes, continue
        </v-chip>
        <v-chip
          size="small"
          variant="tonal"
          color="error"
          class="suggestion-chip"
          :disabled="loadingResponse || isEditingPrompt"
          @click="dismissContinue"
        >
          No
        </v-chip>
      </div>
      <div class="suggestion-chips">
        <button
          type="button"
          class="suggestion-toggle"
          :aria-expanded="showSuggestions"
          @click="showSuggestions = !showSuggestions"
        >
          <v-icon size="14">{{ showSuggestions ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          Try
        </button>
        <template v-if="showSuggestions">
          <v-chip
            v-for="suggestion in suggestions"
            :key="suggestion.prompt"
            size="small"
            variant="tonal"
            color="primary"
            class="suggestion-chip"
            :prepend-icon="suggestion.icon"
            :disabled="loadingResponse || isEditingPrompt"
            @click="submitSuggestion(suggestion.prompt)"
          >
            {{ suggestion.label }}
          </v-chip>
        </template>
      </div>
      <div v-if="isEditingPrompt" class="edit-mode-banner">
        <div class="edit-mode-title">
          Editing an earlier prompt (workflow v{{ editPromptContext?.workflowVersion }})
        </div>
        <div class="edit-mode-subtitle">
          Submitting will restore the workflow to that version and re-run the assistant from there.
        </div>
      </div>
      <v-form ref="chatLogForm" @submit.prevent="updateWorkflowWithAssistant">
        <div class="chat-log-form">
          <v-textarea
            :rules="[rules.required]"
            auto-grow
            rows="3"
            max-rows="12"
            class="no-border"
            variant="outlined"
            v-model="aiDescription"
            ref="promptInputRef"
            placeholder="Prompt"
            @keydown.enter="onKeyDown"
            @keydown.esc="isEditingPrompt ? cancelEditPrompt() : null"
          />
          <div class="d-flex justify-end ga-2">
            <v-btn
              v-if="isEditingPrompt && !loadingResponse"
              type="button"
              size="small"
              variant="outlined"
              color="grey-darken-3"
              prepend-icon="mdi-close"
              @click="cancelEditPrompt"
              :disabled="loadingRequest"
            >
              Cancel
            </v-btn>
            <v-btn
              :type="loadingResponse ? 'button' : 'submit'"
              size="small"
              variant="outlined"
              :color="loadingResponse ? 'error' : 'primary'"
              :prepend-icon="loadingResponse ? 'mdi-stop' : 'mdi-send'"
              :loading="loadingRequest && !loadingResponse"
              :disabled="loadingRequest && !loadingResponse"
              @click="loadingResponse ? stopAssistant() : null"
            >
              {{ loadingResponse ? 'Stop' : (isEditingPrompt ? 'Revert & Re-run' : 'Submit') }}
            </v-btn>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Markdown from 'vue-markdown-render'
import DotsLoader from '@/components/DotsLoader.vue'
import chatLogApi from '@/api/chatLogApi'
import safeStructuredClone from '@/utils/clone'
import { runAiGeneration } from '@/utils/helpHero'
import { runAiWrites } from '@/utils/aiAuthorship'
import { chatLogType } from '@/ai/prompts/common/messagesHistory'
import { useWorkflowAssistantFunctions } from '@/composables/useWorkflowAssistantFunctions'
import { getUpdatesSummaryText, getUpdateSummary } from '@/api/assistant/helpers/updateSummary'
import { getActionLabel } from '@/api/assistant/helpers/actionLabel'
import { sanitizeErrorMessage } from '@/ai/api/aiErrors'
import { appendRoundToTrajectory, pickNarration } from '@/api/assistant/helpers/trajectory'
import { generateWorkflowAssistant, generateFunctionCallResponse, validateWorkflowAssistantAction } from '@/ai'

const props = defineProps({ currentUIState: Object })

const scrollTargetRef = ref(null)
const aiDescription = ref('')
const loadingResponse = ref(false)
const loadingRequest = ref(false)
const chatLogForm = ref(null)
const promptInputRef = ref(null)
const loadingChatLog = ref(false)
const rules = ref({ required: (value) => !!value || 'Required' })
const activeAbortController = ref(null)
const stopRequested = ref(false)
const assistantStatusText = ref('')
// Ordered timeline for the turn: { kind: 'narration' | 'action', text, failed }.
const actionLog = ref([])
// Rendered until the persisted row lands, so the prompt doesn't wait on a network round-trip.
const pendingPrompt = ref('')
const streamingMessage = ref('')
const editPromptContext = ref(null)

const store = useStore()
const { callFunction } = useWorkflowAssistantFunctions()
const workflow = computed(() => store.state.workflow.workflow)
const showSidePanel = computed(() => store.state.showSidePanel)
const user = computed(() => store.state.user.user)
const chatLog = computed(() => ([...store.state.chatLog.chatLog ?? []]).reverse())
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])
const isEditingPrompt = computed(() => !!editPromptContext.value)
// Set when a turn ran out of steps, so the model's "Shall I continue?" is one click away.
const canContinue = ref(false)
const showSuggestions = ref(false)

const suggestions = [
  { label: 'Regenerate Cards', prompt: 'Regenerate all cards on events', icon: 'mdi-auto-fix' },
  { label: 'Add Missing Cards', prompt: 'Add missing cards on events', icon: 'mdi-card-plus' },
  { label: 'Regenerate Command Fields', prompt: 'Regenerate all command fields on events', icon: 'mdi-database-refresh' },
  {
    label: 'Add Missing Command Fields',
    prompt: 'Add missing command fields to Command cards that do not have fields defined',
    icon: 'mdi-database-plus'
  },
  { label: 'Regenerate Read Model Fields', prompt: 'Regenerate all read model fields on events', icon: 'mdi-eye-refresh' },
  {
    label: 'Add Missing Read Model Fields',
    prompt: 'Add missing read model fields to ReadModel cards that do not have fields defined',
    icon: 'mdi-eye-plus'
  },
  { label: 'Regenerate Entities', prompt: 'Regenerate all entities from the workflow', icon: 'mdi-table-refresh' },
  { label: 'Add Missing Entities', prompt: 'Add missing entities from the workflow', icon: 'mdi-table-plus' }
]

const stopAssistant = () => {
  stopRequested.value = true
  assistantStatusText.value = 'Stopping…'
  if (activeAbortController.value) {
    activeAbortController.value.abort()
  }
}

const isUserPrompt = (log) => log?.type === chatLogType.USER_PROMPT || !!log?.userId

// The subscription can commit the persisted row before the mutation resolves, so drop the
// optimistic echo as soon as the real one lands rather than waiting for the response.
const showPendingPrompt = computed(() => {
  if (!pendingPrompt.value) {
    return false
  }

  const last = chatLog.value?.[chatLog.value.length - 1]

  return !(last && isUserPrompt(last) && last.content === pendingPrompt.value)
})

const startEditPromptFromLog = async (log) => {
  if (loadingResponse.value || loadingChatLog.value) {
    return
  }
  if (!log?.id || !log?.workflowVersion) {
    return
  }

  editPromptContext.value = {
    logId: log.id,
    workflowVersion: log.workflowVersion,
    originalPrompt: log?.content || ''
  }
  aiDescription.value = log?.content || ''
  await nextTick()
  promptInputRef.value?.focus?.()
}

const cancelEditPrompt = async () => {
  editPromptContext.value = null
  aiDescription.value = ''
  await nextTick()
  promptInputRef.value?.focus?.()
}

const getMessagesHistoryBeforeLogId = (messages, logId) => {
  if (!Array.isArray(messages) || !logId) {
    return Array.isArray(messages) ? messages : []
  }

  const idx = messages.findIndex((m) => m?.id === logId)
  if (idx === -1) {
    return messages
  }

  return messages.slice(0, idx)
}

const getChatLogTypeForReplay = (log) => {
  if (log?.type) {
    return log.type
  }
  return log?.userId ? chatLogType.USER_PROMPT : chatLogType.AI_TEXT_RESPONSE
}

const truncateChatLogUITo = (entriesChronological) => {
  const newestFirst = Array.isArray(entriesChronological)
    ? [...entriesChronological].reverse()
    : []
  store.commit('chatLog/setChatLog', newestFirst)
}

const persistTruncatedChatLogSilently = async (entriesChronological) => {
  if (!workflow.value?.projectId || !workflow.value?.id) {
    throw new Error('Missing workflow info for chat log replay')
  }

  // Suppress subscription + local commits while we rebuild server state.
  store.commit('chatLog/setSuppressIncoming', true)
  try {
    await chatLogApi.clearChatLog({
      projectId: workflow.value.projectId,
      workflowId: workflow.value.id,
      silent: true
    })

    if (!Array.isArray(entriesChronological) || entriesChronological.length === 0) {
      return
    }

    const rebuiltChronological = []
    for (const entry of entriesChronological) {
      const type = getChatLogTypeForReplay(entry)
      const content = entry?.content ?? ''

      const created = await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type,
          content,
          userId: entry?.userId,
          aiOutput: entry?.aiOutput,
          workflowVersion: entry?.workflowVersion
        },
        silent: true
      })

      if (created?.id) {
        rebuiltChronological.push(created)
      }
    }

    // Swap the local store to match server state (new ids) in one go to avoid occasional duplicates
    // from delayed subscription deliveries.
    const newestFirst = [...rebuiltChronological].reverse()
    store.commit('chatLog/setChatLog', newestFirst)
  } finally {
    store.commit('chatLog/setSuppressIncoming', false)
  }
}

const restoreWorkflowForEdit = async (restoreVersion) => {
  if (!restoreVersion || !workflow.value?.version) {
    return false
  }

  const beforeVersion = workflow.value.version
  await store.dispatch('workflow/restorePreviousWorkflowVersion', {
    restoreVersion,
    workflow: workflow.value
  })

  await nextTick()
  const after = store.state.workflow.workflow
  if (!after) {
    return false
  }

  // Prefer checking sameAsVersion when available; otherwise fall back to detecting a version change.
  if (after.sameAsVersion === restoreVersion) {
    return true
  }

  return after.version !== beforeVersion
}

const MAX_AGENTIC_ROUNDS = 12

// App text, not the summary turn's job — that turn is told every listed action completed.
const STOPPED_NOTICE = 'Stopped at your request — the rest wasn\'t carried out.'
const CAP_NOTICE = 'There may be more to do — shall I continue?'
// An affirmative to whatever the assistant just offered; its question is in the replayed history.
const CONTINUE_PROMPT = 'Yes, go ahead.'

// What the Yes/No chips reply to. Test the offer itself — app blocks are appended after it.
const endsWithQuestion = (text) => /\?\s*$/.test(String(text || '').trim())

const appendBlock = (base, block) => base ? `${base}\n\n---\n\n${block}` : block

// The round count is a harness detail: useful while working on the loop, noise for the user.
const thinkingLabel = (step) => process.env.NODE_ENV === 'production'
  ? 'Thinking…'
  : `Thinking… (step ${step} of ${MAX_AGENTIC_ROUNDS})`

const executeToolRound = async (outputItems, abortController) => {
  const calls = []

  for (const output of outputItems) {
    if (stopRequested.value || abortController.signal.aborted) {
      break
    }

    if (output.type !== 'function_call') {
      continue
    }

    const functionName = output.name
    let args = {}
    let argsError = null
    try {
      args = output.arguments ? JSON.parse(output.arguments) : {}
    } catch (parseError) {
      argsError = `Invalid arguments for ${functionName}`
    }

    // Logged before it runs so the loader sits under its own line.
    const entry = { kind: 'action', text: getActionLabel(functionName, args), failed: false }
    actionLog.value.push(entry)
    assistantStatusText.value = ''

    // The reason goes on the timeline entry too, so a failure is legible where it happened
    // rather than only in the recap at the end.
    const fail = (message) => {
      entry.failed = true
      entry.error = message
      calls.push({ item: output, result: { success: false, error: message } })
    }

    if (argsError) {
      fail(argsError)
      continue
    }

    const additionalArguments = {
      workflow: workflow.value,
      svgGraph: safeStructuredClone(svgGraph.value),
      svgLanes: safeStructuredClone(svgLanes.value),
      svgGroups: safeStructuredClone(svgGroups.value),
      cardTypes: safeStructuredClone(cardTypes.value)
    }

    const validationResult = validateWorkflowAssistantAction(functionName, args, additionalArguments)
    if (!validationResult.valid) {
      fail(validationResult.error)
      continue
    }

    if (stopRequested.value || abortController.signal.aborted) {
      actionLog.value.pop()
      break
    }

    let result
    try {
      result = await callFunction(functionName, args, {
        signal: abortController.signal,
        shouldStop: () => stopRequested.value
      })
    } catch (error) {
      // An uncaught abort would otherwise kill the turn and its recap.
      if (error?.name === 'AbortError' || stopRequested.value || abortController.signal.aborted) {
        actionLog.value.pop()
        break
      }

      // A nested AI call can throw a raw provider payload; it must not reach the chat log.
      fail(sanitizeErrorMessage(error) || `Could not complete ${functionName}`)
      continue
    }

    const stopped = stopRequested.value || abortController.signal.aborted

    if (!result || result.success === false) {
      // A caught abort surfaces as a failure; don't tell the user to revise what they cancelled.
      if (stopped) {
        actionLog.value.pop()
        break
      }

      fail(result?.error || `Could not complete ${functionName}`)
      continue
    }

    // A result that landed before the stop still happened — keep it, so the recap is honest.
    calls.push({ item: output, result, summary: getUpdateSummary(functionName, args, additionalArguments, result) || null })
  }

  return calls
}

// The turn counts as one AI action, but only applying the tool calls is the AI writing: a
// person editing by hand while the model thinks is not.
const updateWorkflowWithAssistant = () => runAiGeneration(runAssistantTurn, { writesAsAi: false })

const runAssistantTurn = async () => {
  try {
    const validationResult = await chatLogForm.value.validate()
    if (!validationResult?.valid) {
      return
    }

    stopRequested.value = false
    canContinue.value = false
    actionLog.value = []
    assistantStatusText.value = thinkingLabel(1)
    const abortController = new AbortController()
    activeAbortController.value = abortController

    loadingRequest.value = true
    loadingResponse.value = true
    const editContextSnapshot = editPromptContext.value
    const prompt = aiDescription.value
    resetForm()

    const fullMessagesHistory = safeStructuredClone(chatLog.value)
    let messagesHistory = fullMessagesHistory
    if (editContextSnapshot?.logId) {
      messagesHistory = getMessagesHistoryBeforeLogId(fullMessagesHistory, editContextSnapshot.logId)
    }

    if (editContextSnapshot?.workflowVersion) {
      const restoreVersion = editContextSnapshot.workflowVersion
      const currentVersion = workflow.value?.version

      if (currentVersion && restoreVersion > currentVersion) {
        store.commit('snackbar/showMessage', {
          content: `Cannot restore to version ${restoreVersion}. Current version is ${currentVersion}.`,
          timeout: 5000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        aiDescription.value = prompt
        await nextTick()
        promptInputRef.value?.focus?.()
        return
      }

      truncateChatLogUITo(messagesHistory)

      assistantStatusText.value = 'Restoring chat history…'
      await persistTruncatedChatLogSilently(messagesHistory)

      if (currentVersion && restoreVersion < currentVersion) {
        assistantStatusText.value = `Restoring workflow to version ${restoreVersion}…`
        const restored = await restoreWorkflowForEdit(restoreVersion)
        if (!restored) {
          aiDescription.value = prompt
          await nextTick()
          promptInputRef.value?.focus?.()
          return
        }
      }

      editPromptContext.value = null
    }

    const workflowVersionAtPrompt = workflow.value?.version ?? null

    pendingPrompt.value = prompt

    const created = await chatLogApi.createChatLog({
      projectId: workflow.value.projectId,
      input: {
        workflowId: workflow.value.id,
        type: chatLogType.USER_PROMPT,
        content: prompt,
        userId: user.value?.id,
        workflowVersion: workflowVersionAtPrompt
      }
    })

    // The store committed the real row inside that await, so the swap renders as one frame.
    pendingPrompt.value = ''

    if (!created?.id) {
      store.commit('snackbar/showMessage', {
        content: 'Failed to save prompt. Please try again.',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      return
    }

    loadingRequest.value = false

    // Frozen for the whole turn on purpose: rounds learn state changes from tool results, not a refreshed spec.
    let workflowSnapshot = { ...workflow.value, cardTypes: cardTypes.value }
    const uiStateSnapshot = safeStructuredClone(props.currentUIState)

    let response = await generateWorkflowAssistant({
      workflow: workflowSnapshot,
      messagesHistory,
      prompt,
      currentUIState: uiStateSnapshot,
      abortController,
      onRetry: onAiRetry
    })

    if (stopRequested.value || abortController.signal.aborted) {
      return
    }

    if (response?.error) {
      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: response.error.message,
          aiOutput: null
        }
      })
      return
    }

    if (!response?.output?.length) {
      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: 'I could not generate a response this time (empty output). Please try again.',
          aiOutput: null
        }
      })
      return
    }

    const allCalls = []
    const trajectory = []
    let lastRoundCalls = []
    let round = 0
    let expectedVersion = workflowSnapshot.version ?? null
    let collaboratorDrift = false
    let snapshotRefreshNeeded = false
    let exitNotice = ''
    // '' | 'cap' | 'provider' | 'empty' | 'stopped'; non-empty means the turn ended early.
    let exitKind = ''

    while (true) {
      const functionCalls = response.output.filter((output) => output.type === 'function_call')

      if (functionCalls.length === 0) {
        if (allCalls.length === 0) {
          for (const output of response.output) {
            if (output.type !== 'message') {
              continue
            }
            const outputText = output.content.find((item) => item.type === 'output_text')?.text
            if (!outputText) {
              continue
            }
            assistantStatusText.value = ''
            await chatLogApi.createChatLog({
              projectId: workflow.value.projectId,
              input: {
                workflowId: workflow.value.id,
                type: chatLogType.AI_TEXT_RESPONSE,
                content: outputText,
                aiOutput: JSON.stringify(response.output)
              }
            })
          }
          return
        }
        break
      }

      if ((workflow.value?.version ?? null) !== expectedVersion) {
        collaboratorDrift = true
        snapshotRefreshNeeded = true
      }

      const narration = pickNarration(response.output)
      if (narration) {
        actionLog.value.push({ kind: 'narration', text: narration })
      }

      lastRoundCalls = await runAiWrites(() => executeToolRound(response.output, abortController))
      allCalls.push(...lastRoundCalls)
      expectedVersion = workflow.value?.version ?? null

      appendRoundToTrajectory(trajectory, response.output, lastRoundCalls)

      if (stopRequested.value || abortController.signal.aborted) {
        exitKind = 'stopped'
        exitNotice = STOPPED_NOTICE
        break
      }

      round++
      if (round >= MAX_AGENTIC_ROUNDS) {
        // CAP_NOTICE is the fallback if the summary turn produces nothing.
        exitKind = 'cap'
        exitNotice = CAP_NOTICE
        break
      }

      assistantStatusText.value = thinkingLabel(round + 1)
      if (snapshotRefreshNeeded) {
        workflowSnapshot = { ...workflow.value, cardTypes: cardTypes.value }
        snapshotRefreshNeeded = false
      }
      response = await generateWorkflowAssistant({
        workflow: workflowSnapshot,
        messagesHistory,
        prompt,
        currentUIState: uiStateSnapshot,
        abortController,
        currentTurnOutputs: trajectory,
        collaboratorDrift,
        onRetry: onAiRetry
      })

      if (stopRequested.value || abortController.signal.aborted) {
        exitKind = 'stopped'
        exitNotice = STOPPED_NOTICE
        break
      }

      if (response?.error) {
        exitKind = 'provider'
        exitNotice = `I couldn't finish this request. ${response.error.message}`
        break
      }

      if (!response?.output?.length) {
        exitKind = 'empty'
        exitNotice = 'The AI service returned an empty response partway through — shall I carry on?'
        break
      }
    }

    const successfulCalls = allCalls.filter((call) => call.result && call.result.success !== false)
    // The tool name rides along so the recap can collapse repeats; the model gets plain text.
    const updates = allCalls
      .filter((call) => call.summary)
      .map((call) => ({ name: call.item.name, text: call.summary }))
    const updatesSummary = updates.map((update) => update.text)
    const unresolvedFailures = lastRoundCalls.filter((call) => !call.result || call.result.success === false)
    const failureText = unresolvedFailures.map((call) => `**${call.result?.error || 'Action failed'}**`).join('\n\n')

    if (successfulCalls.length > 0) {
      // Stop means stop: no summary call against an aborted signal, and no sign-off.
      // A stop gets no summary at all — the user watched it happen and chose to end it. The
      // row is still written so the next turn's history knows which calls were made.
      const stopped = exitKind === 'stopped'
      let summaryContent = stopped ? '' : getUpdatesSummaryText(updates)
      let offersToCarryOn = false

      if (!stopped) {
        try {
          assistantStatusText.value = 'Writing summary…'
          const aiText = await generateFunctionCallResponse({
            workflow: workflow.value,
            prompt,
            updatesSummary,
            incomplete: !!exitKind,
            onDelta: (text) => {
              assistantStatusText.value = ''
              streamingMessage.value = text
            },
            abortController
          })

          if (aiText && !stopRequested.value) {
            summaryContent = aiText
            offersToCarryOn = endsWithQuestion(aiText)

            // The model named what remains; other exits still need their concrete reason.
            if (exitKind === 'cap') {
              exitNotice = ''
            }
          }
        } catch (error) {
          // Abort is a normal outcome when the user clicks Stop.
          if (stopRequested.value || abortController.signal.aborted || error?.name === 'AbortError') {
            return
          }

          console.error('Error generating AI response, using fallback:', error)
        }
      }

      if (failureText) {
        // Listed for honesty, with no call to action: work still succeeded, so the model
        // either worked around these or judged them unnecessary.
        summaryContent = appendBlock(summaryContent, `Some actions couldn't be completed:\n\n${failureText}`)
      }

      if (exitNotice) {
        summaryContent = appendBlock(summaryContent, exitNotice)
      }

      canContinue.value = !!exitKind && (offersToCarryOn || endsWithQuestion(exitNotice))

      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_FUNCTION_CALL,
          content: summaryContent,
          aiOutput: JSON.stringify({
            // thoughtSignature is a Gemini in-turn artifact; replaying it cross-provider breaks OpenAI.
            output: allCalls.map((call) => ({ ...call.item, thoughtSignature: undefined })),
            functionResults: allCalls.map((call) => ({
              callId: call.item.call_id,
              result: call.result?.success === false
                ? { success: false, error: call.result.error }
                : { success: true }
            }))
          })
        }
      })

      streamingMessage.value = ''
    } else if (failureText || exitNotice) {
      const parts = []
      if (failureText) {
        const advice = exitNotice ? '' : '\n\nPlease revise your request based on these constraints.'
        parts.push(`I encountered some issues with your request:\n\n${failureText}${advice}`)
      }
      if (exitNotice) {
        parts.push(exitNotice)
      }

      const content = parts.join('\n\n---\n\n')
      canContinue.value = !!exitKind && endsWithQuestion(exitNotice)

      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content,
          aiOutput: null
        }
      })
    }
  } catch (e) {
    // Abort is a normal outcome when the user clicks Stop.
    if (stopRequested.value || activeAbortController.value?.signal?.aborted || e?.name === 'AbortError') {
      return
    }

    try {
      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: `Something went wrong while running the AI assistant.\n\n${e?.name || 'Error'}: ${e?.message || String(e)}\n\nPlease try again.`,
          aiOutput: null
        }
      })
    } catch (logError) {
      console.error('Failed to write assistant error to chat log:', logError)
    }
  } finally {
    activeAbortController.value = null
    stopRequested.value = false
    assistantStatusText.value = ''
    actionLog.value = []
    pendingPrompt.value = ''
    streamingMessage.value = ''
    loadingResponse.value = false
  }
}

// Declining costs nothing: no model call, no chat-log row.
const dismissContinue = () => {
  canContinue.value = false
}

// A silent 30s wait reads as a hang, so name the reason while we back off.
const onAiRetry = ({ waitMs }) => {
  assistantStatusText.value = `The AI service is busy — retrying in ${Math.ceil(waitMs / 1000)}s…`
}

const submitSuggestion = async (prompt) => {
  if (isEditingPrompt.value) {
    return
  }
  aiDescription.value = prompt
  await nextTick()
  await updateWorkflowWithAssistant()
}

const onKeyDown = (event) => {
  if (event.shiftKey) {
    return
  }

  event.preventDefault()
  updateWorkflowWithAssistant()
}

const getChatLog = async (workflowId) => {
  loadingChatLog.value = true
  await chatLogApi.getChatLog({ workflowId })
  loadingChatLog.value = false
}

const resetForm = () => {
  aiDescription.value = ''
  nextTick(() => chatLogForm.value.resetValidation())
}

watch(() => workflow.value.id, () => {
  // Unfinished work belongs to the workflow it happened in.
  canContinue.value = false

  if (workflow.value.id) {
    getChatLog(workflow.value.id)
  }
}, { immediate: true })

watch([
  () => chatLog.value?.length,
  () => showSidePanel.value,
  () => loadingChatLog.value,
  () => streamingMessage.value,
  () => actionLog.value.length,
  () => pendingPrompt.value
], async ([_, showPanel, loading]) => {
  if (scrollTargetRef.value && showPanel && !loading) {
    await nextTick()
    scrollTargetRef.value.scrollIntoView({ behavior: 'smooth' })
  }
}, { immediate: true })
</script>

<style scoped>
.chat-log-container {
  height: calc(100vh - 134px);
}

.chat-log-list-wrapper {
  flex-grow: 1;
  margin-bottom: 20px;
  padding-right: 12px;
  overflow-y: auto;
}

.chat-log-list {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding-bottom: 16px;
}

.prompt {
  display: flex;
}

.user-prompt {
  justify-content: flex-end;
}

.prompt-box {
  width: fit-content;
  padding: 4px;
  transition: all 0.15s;

  &.user-prompt-box {
    margin-right: 2px;
    padding: 12px 16px;
    border-radius: 16px 16px 4px 16px;
    background: #eeeeee;
    box-shadow: 3px 3px 5px 0 rgba(0, 0, 0, 0.1);
    overflow-wrap: anywhere;
  }

  [data-theme="dark"] &.user-prompt-box {
    background: var(--light-4);
  }

  &.ai-response-box {
    margin-left: 2px;
    padding: 0;
    border-radius: 16px;

    &:first-child {
      margin-top: 2px;
    }
  }
}

.chat-log-content {
  white-space: pre-line;
}

.user-prompt-row {
  position: relative;
  max-width: 80%;
}

.user-prompt-row :deep(.edit-btn.v-btn) {
  position: absolute;
  bottom: -32px;
  right: 8px;
  min-width: 0;
  height: 28px !important;
  padding: 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  box-shadow: none;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.user-prompt-row:hover :deep(.edit-btn.v-btn) {
  opacity: 1;
}

.edit-mode-banner {
  border: 1px solid #e0e0e0;
  background: var(--light-1);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.edit-mode-title {
  font-size: 12px;
  font-weight: 600;
  color: #4f5359;
}

.edit-mode-subtitle {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

/* Nine chips wrap to three rows; collapsed by default so the panel keeps its height for chat. */
.suggestion-toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 12px;
  color: var(--text-light);
  cursor: pointer;
}

.suggestion-toggle:hover {
  text-decoration: underline;
}

.suggestion-chip {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.assistant-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 6px;
}

.assistant-status-text,
.assistant-thinking-text {
  font-size: 12px;
  color: var(--text-light);
  user-select: none;
  /* A $ref in an error message is one unbreakable token; without this the panel scrolls. */
  overflow-wrap: anywhere;
}

.assistant-thinking-text {
  font-style: italic;
  line-height: 1.5;
  white-space: pre-wrap;
}

/* Attempted and rejected — not removed, which is what a strikethrough would imply here.
   The marker carries the meaning too, so it does not rest on colour alone. */
.assistant-status-failed {
  color: rgb(var(--v-theme-error));
}

.assistant-status-failed::before {
  content: '✕ ';
}

.assistant-status-reason {
  padding-left: 14px;
  line-height: 1.5;
  opacity: 0.9;
}

.assistant-status-row {
  display: flex;
  align-items: center;
  column-gap: 8px;
}

.chat-log-form {
  padding: 8px;
  border: 1px solid #dddddd;
  border-radius: 4px;

  & .v-btn.v-btn--size-small.v-btn--variant-outlined {
    padding: 5px 12px !important;
  }
}

:deep(.v-textarea) {
  & .v-field {
    & .v-field__outline__start,
    & .v-field__outline__end {
      border: none
    }

    & .v-field__input {
      padding: 4px;
      mask-image: none;
    }

  }

  & .v-input__details {
    display: none;
  }
}

:deep(.markdown-content) {
  margin: 16px 0;
  line-height: 1.6;
  /* A $ref is one unbreakable token; without this the whole panel scrolls sideways. */
  overflow-wrap: anywhere;

  ul {
    list-style-type: disc;
    margin: 8px 0;
    padding-left: 20px;
  }

  ol {
    list-style-type: decimal;
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  h1, h2, h3, h4 {
    margin: 16px 0 8px;
    font-weight: 600;
  }

  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  code {
    overflow-wrap: anywhere;
  }

  table {
    display: block;
    overflow-x: auto;
  }
}
</style>
